import type { GroceryItem } from './protocolTypes'
import {
  aggregateRawIngredients,
  groupAggregatedIngredients,
  type AggregatedIngredient,
  type GroupedShoppingList,
  type MacroBucket,
  MACRO_BUCKET_ORDER,
  resolveIngredient,
} from './ingredientRegistry'
import { mergeQuantityMass } from './quantityMass'
import { resolvePackSize, parseQuantityToGrams } from './packSizeResolver'

export type AggregatedGroceryItem = AggregatedIngredient & {
  id: string
  source_ids: string[]
  approx_cost: number
  store_chain: string
  store_distance_km: number | null
  store_address: string | null
  retailer_name: string | null
  is_estimated: boolean
  preferred_store_name: string | null
  is_secured: boolean
  amount_remaining?: number | null
  is_depleted?: boolean
  coaching_reason?: string | null
  shopping_pack?: {
    label: string
    packs_needed: number
    pack_amount: number
    pack_unit: string
    weekly_total: number
  }
}

export type GroupedGroceryManifest = Record<MacroBucket, AggregatedGroceryItem[]>

export function aggregateGroceryManifest(items: GroceryItem[]): AggregatedGroceryItem[] {
  const map = new Map<string, AggregatedGroceryItem>()

  for (const item of items) {
    const resolved = resolveIngredient(item.item_name)
    const key = `${resolved.macro}::${resolved.canonicalName.toLowerCase()}`
    const prev = map.get(key)
    const qty = item.quantity_mass?.trim() || 'as needed'

    if (!prev) {
      map.set(key, {
        id: item.id,
        source_ids: [item.id],
        canonicalName: resolved.canonicalName,
        quantity_mass: qty,
        macro: resolved.macro,
        macro_category: resolved.macro,
        approx_cost: item.approx_cost,
        store_chain: item.store_chain,
        store_distance_km: item.store_distance_km,
        store_address: item.store_address,
        retailer_name: item.retailer_name,
        is_estimated: item.is_estimated,
        preferred_store_name: item.preferred_store_name,
        is_secured: item.is_secured,
        amount_remaining: item.amount_remaining ?? null,
        is_depleted: item.is_depleted ?? false,
        coaching_reason: item.coaching_reason ?? null,
      })
      continue
    }

    const distance = item.store_distance_km ?? Infinity
    const prevDistance = prev.store_distance_km ?? Infinity
    const useNewStore = distance < prevDistance

    map.set(key, {
      ...prev,
      source_ids: [...prev.source_ids, item.id],
      quantity_mass: mergeQuantityMass(prev.quantity_mass, qty),
      approx_cost: Math.round((prev.approx_cost + item.approx_cost) * 100) / 100,
      store_chain: useNewStore ? item.store_chain : prev.store_chain,
      store_distance_km: useNewStore ? item.store_distance_km : prev.store_distance_km,
      store_address: useNewStore ? item.store_address : prev.store_address,
      retailer_name: useNewStore ? item.retailer_name : prev.retailer_name,
      is_estimated: useNewStore ? item.is_estimated : prev.is_estimated,
      preferred_store_name: useNewStore ? item.preferred_store_name : prev.preferred_store_name,
      is_secured: prev.is_secured && item.is_secured,
      amount_remaining:
        prev.amount_remaining != null && item.amount_remaining != null
          ? Math.min(prev.amount_remaining, item.amount_remaining)
          : (prev.amount_remaining ?? item.amount_remaining ?? null),
      is_depleted: Boolean(prev.is_depleted && item.is_depleted),
      coaching_reason: prev.coaching_reason ?? item.coaching_reason ?? null,
    })
  }

  const sorted = [...map.values()].sort((a, b) => a.canonicalName.localeCompare(b.canonicalName))

  // Safety-net: deduplicate by canonicalName in case two macro buckets resolved the same ingredient
  const seen = new Set<string>()
  const deduped = sorted.filter((item) => {
    const key = item.canonicalName.toLowerCase().trim()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  return deduped.map((item) => {
    const { grams, unit } = parseQuantityToGrams(item.quantity_mass)
    const resolved = resolvePackSize(item.canonicalName, grams, unit)
    return {
      ...item,
      shopping_pack: {
        label: resolved.shopping_label,
        packs_needed: resolved.packs_needed,
        pack_amount: resolved.recommended_pack.amount,
        pack_unit: resolved.recommended_pack.unit,
        weekly_total: resolved.weekly_total_g_or_ml,
      },
    }
  })
}

export function groupGroceryManifest(items: GroceryItem[]): GroupedGroceryManifest {
  const aggregated = aggregateGroceryManifest(items)
  const grouped: GroupedGroceryManifest = {
    proteins: [],
    carbs: [],
    fats: [],
    produce_micros: [],
  }

  for (const item of aggregated) {
    grouped[item.macro].push(item)
  }

  for (const bucket of MACRO_BUCKET_ORDER) {
    grouped[bucket].sort((a, b) => a.canonicalName.localeCompare(b.canonicalName))
  }

  return grouped
}

export function flattenGroupedManifest(grouped: GroupedGroceryManifest): AggregatedGroceryItem[] {
  return MACRO_BUCKET_ORDER.flatMap((bucket) => grouped[bucket])
}

export { MACRO_BUCKET_ORDER, type GroupedShoppingList, groupAggregatedIngredients, aggregateRawIngredients }
